/**
 * Route path constants for programmatic navigation and externally-facing URLs
 * (e.g. Stripe Checkout return URLs). Keep in sync with app.routes.ts — no
 * hardcoded path strings at call sites.
 */
export const AppRoutes = {
  /**
   * Where `managementAccessGuard` sends everyone once SmartPlayr has closed the management app to
   * the venue. Outside the dashboard shell, like the terms gate, and for the same reason.
   */
  AccessClosed: '/access-closed',
  Campaigns: '/campaigns',
  CampaignNew: '/campaigns/new',
  /** The venue's coach catalog; behind the COACHING flag. */
  Coaches: '/coaches',
  /** Codes a player types at checkout for a percentage off. */
  DiscountCodes: '/discount-codes',
  DiscountCodeNew: '/discount-codes/new',
  Events: '/events',
  EventNew: '/events/new',
  Home: '/',
  /** Everything about money: how the venue gets paid, and the documents that follow. */
  Invoicing: '/invoicing',
  /** The acceptance gate. Lives outside the dashboard shell — see partner-terms.guard.ts. */
  PartnerTerms: '/partner-terms',
  Settings: '/settings',
  /**
   * The venue's colours, on a page of their own: the editor repaints the app live, so the page
   * is the preview and it has exactly one Save. Reached from the Settings row and the header.
   */
  SettingsAppearance: '/settings/appearance',
} as const;

export function discountCodeEdit(id: string): string {
  return `${AppRoutes.DiscountCodes}/${id}/edit`;
}

export function campaignDetail(id: string): string {
  return `${AppRoutes.Campaigns}/${id}`;
}

export function eventDetail(id: string): string {
  return `${AppRoutes.Events}/${id}`;
}

export function eventEdit(id: string): string {
  return `${AppRoutes.Events}/${id}/edit`;
}

/** Query-param names that form part of a URL contract (e.g. Stripe return URLs). */
export const QueryParams = {
  CreditsPurchased: 'credits_purchased',
  /**
   * Narrows the events list, and preselects the kind on the create form. It is in the URL
   * rather than in a signal so the nav's "Tournaments" entry, the back button and a
   * bookmarked filter all mean the same thing.
   */
  EventKind: 'kind',
  /** The events list's search term, kept in the URL for the same reason the kind is. */
  EventSearch: 'q',
  /** The events list's page, 1-based; absent means the first. */
  EventPage: 'page',
  /**
   * Opens the payments & invoicing page on one of its tabs. Its value is an `InvoicingTabs`
   * member; anything else is ignored and the page opens where it always does.
   */
  InvoicingTab: 'tab',
} as const;

/**
 * The tabs on the payments & invoicing page, as they are written in its URL.
 *
 * Named here rather than inside the page because they are a link target: the setup checklist
 * sends an owner straight to Payments, and a literal on either side is how the two drift apart.
 */
export const InvoicingTabs = {
  Documents: 'documents',
  Payments: 'payments',
  Payouts: 'payouts',
  Settings: 'settings',
} as const;

export type InvoicingTab = (typeof InvoicingTabs)[keyof typeof InvoicingTabs];

/** True when a query-string value names a tab the page actually has. */
export function isInvoicingTab(value: string | null | undefined): value is InvoicingTab {
  return !!value && (Object.values(InvoicingTabs) as string[]).includes(value);
}

/**
 * The events list showing one kind only — what the nav's "Tournaments" entry now points at,
 * and where `/tournaments` lands.
 */
export function eventsOfKind(kind: string): string {
  return `${AppRoutes.Events}?${QueryParams.EventKind}=${kind}`;
}

/** The create form with the kind preselected (and frozen, as it is at creation). */
export function newEventOfKind(kind: string): string {
  return `${AppRoutes.EventNew}?${QueryParams.EventKind}=${kind}`;
}
